import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { authSet, getStaffUid, getUid } from "../redux/authSlice";
import React, { useCallback, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import { firebaseApp } from "../firebase/app";
import DashboardLayout from "../layout/dashboard-layout";
import Login from "../pages/login";
import SkeletonLayout from "../components/skeletons/layout";
import { getData } from "./firebase";
import { getTokenFromUrl } from "./utils.service";
import { dealerNameSet, dealershipNameSet, getTimeZone, timeZoneSet } from "../redux/userSlice";
import { usePresence } from "../hooks/usePresence";
import DashboardV2Modal from "../sections/dashboard/dashboard-v2-modal";

const PrivateRoute = () => {
  const branchUid = useSelector(getUid) as string | null;
  const staffUid = useSelector(getStaffUid) as string | null;
  const auth = branchUid;
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current route is login
  const isLoginRoute = location.pathname === "/login";

  const timeZone = useSelector(getTimeZone);

  // Initialize presence tracking for authenticated users
  // This tracks online/offline status for round-robin lead assignment
  usePresence(branchUid, staffUid, !!branchUid && !!staffUid);

  const handleUnauthorize = useCallback(async () => {
    // Note: setOffline is handled by logoutUser() in logout.tsx/navbar.tsx
    // This function is called when auth state changes to null (already signed out)
    // so we just clean up the local state here

    setLoading(false);
    dispatch(authSet(undefined));
    navigate("/login");
  }, [dispatch, navigate]);

  const handleLoginWithCustomToken = useCallback(
    (token: string) => {
      const auth = getAuth(firebaseApp);
      // dispatch(isActiveExtensionToggled(true));
      signInWithCustomToken(auth, token)
        .then((r) => {
          console.log(r);
        })
        .catch((e) => {
          handleUnauthorize();
          console.log(e);
        });
    },
    [handleUnauthorize]
  );

  const getDealershipTimeZone = useCallback(
    (uidValue: string) => {
      getData(`dealers/${uidValue}/info/timeZone`).then((data: any) => {
        if (data) {
          dispatch(timeZoneSet(data));
        } else {
          dispatch(timeZoneSet(""));
        }
      });
    },
    [dispatch]
  );

  const getDealershipName = useCallback(
    (uidValue: string) => {
      if (!uidValue) return;
      const path = `dealers/${uidValue}/info/dealerName`;
      getData(path)
        .then((data: any) => {
          if (data) {
            dispatch(dealershipNameSet(data));
          }
        })
        .catch((error) => console.log(error, `error getting (${path}))`));
    },
    [dispatch]
  );

  const getDealerName = useCallback(
    (uid: string, mainUid: string) => {
      if (!uid || !mainUid) return;
      const path = `/dealers/${mainUid}/info/users/${uid}`;
      getData(path)
        .then((data: any) => {
          if (data) {
            dispatch(dealerNameSet(data?.displayName));
          }
        })
        .catch((error) => console.log(error, `error getting (${path}))`));
    },
    [dispatch]
  );

  const postAuthState = useCallback(
    async (user: any) => {
      if (user) {
        // Access custom claims
        try {
          const idTokenResult = await user.getIdTokenResult();
          const claims = idTokenResult.claims;
          const { uid, displayName, accessToken, role } = user;
          const path = `/dealers/${branchUid || uid}/info/buyAgent`;
          const userpath = `/Users/${uid}`;

          const userRoleTitle = await getData(userpath).then((data: any) => data?.role);

          // console.log("user:", user);

          const pathForDealerName = `/dealers/${branchUid || uid}/info/dealerName`;
          const dealerFinalName = await getData(pathForDealerName);
          console.log(dealerFinalName);
          getDealershipTimeZone(branchUid || uid);

          getData(path)
            .then((data) => {
              const userData = {
                displayName: claims.name ?? displayName,
                uid: claims.managerUID ?? uid,
                // uid,
                token: accessToken,
                buyAgent: !!data,
                role: claims.role ?? role ?? "manager",
                roleTitle: userRoleTitle || (claims.role ?? role),
                staffUid: uid,
              };
              dispatch(authSet(userData));
              // dispatch(dealerNameSet(claims?.name ? claims?.name : displayName));
              getDealershipName(claims.managerUID ?? uid);
              getDealerName(uid, branchUid || "");
              // TODO:  set staff uid

              setLoading(false);
            })
            .catch((error) => {
              // console.log(`error getting ${path}`, error)``;
              console.log(error);
            });
        } catch (error) {
          console.error("Error getting claims:", error);
        }
      } else {
        const token = getTokenFromUrl("search");
        if (token && token !== "") {
          handleLoginWithCustomToken(token);
        } else {
          handleUnauthorize();
        }
      }
    },
    [
      branchUid,
      dispatch,
      getDealerName,
      getDealershipName,
      getDealershipTimeZone,
      handleLoginWithCustomToken,
      handleUnauthorize,
    ]
  );

  useEffect(() => {
    const auth = getAuth(firebaseApp);

    onAuthStateChanged(auth, postAuthState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate, branchUid]);

  // useEffect(() => {
  //   if (!auth) {
  //     navigate("/login");
  //   }
  // }, [auth, navigate]);

  // if (loading) return <PageLoading fullScreen />;
  if (loading) return <SkeletonLayout />;

  // If user is not authenticated and not on login page, redirect to login
  if (!auth && !isLoginRoute) return <Login />;

  // If user is authenticated and on login page, redirect to dashboard
  if (auth && isLoginRoute && timeZone) {
    navigate("/", { replace: true });
    return null;
  }

  // If on login page and not authenticated, show login
  if (isLoginRoute && !auth) return <Login />;

  return (
    <DashboardLayout>
      <DashboardV2Modal />
      <Outlet />
    </DashboardLayout>
    // <PageLoading />
    // <SkeletonLayout />
  );
};

export default PrivateRoute;
